import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

/** How long after the reader's last touch a scroll still counts as theirs. Covers a flick's
 *  momentum: the first frames of it already carry them past the threshold. */
const INTENT_WINDOW_MS = 1000;

interface StickToBottomItem {
  id: string;
  textLength: number;
}

interface UseStickToBottomOptions<TScroller extends HTMLElement = HTMLElement> {
  /** Scrollable container. Either pass a ref or let `attach` find the nearest ancestor of the anchor. */
  scrollerRef?: RefObject<TScroller | null>;
  /** The growing tail of the conversation — last message id + content length feeds the follow-on-stream behaviour. */
  tail: StickToBottomItem | undefined;
  /** Total messages length — drives the "new message arrived" force-stick. */
  count: number;
  /** Distance from the bottom (px) below which the user counts as "parked at bottom" and we keep auto-scrolling. */
  threshold?: number;
  /**
   * Distance from the bottom (px) beyond which the reader counts as genuinely
   * AWAY — reading history, not just nudged off the tail. Drives
   * `isFarFromBottom`, the right gate for a "back to latest" affordance:
   * showing it the moment `threshold` breaks (80px) means it pops over content
   * on the slightest scroll, which reads as nagging.
   */
  awayThreshold?: number;
  /**
   * When false, a new message arriving does NOT yank a scrolled-up reader back
   * to the bottom — it only follows the tail while the reader is already
   * parked there. Surfaces that offer a "Jump to latest" pill (team-space)
   * pass false; default true preserves the original force-stick behaviour.
   */
  forceStickOnNew?: boolean;
  /**
   * Identity of the current conversation. When it changes the hook treats it as
   * a fresh open — re-arms the bottom-stick, scrolls to the newest message, and
   * clears `isAtBottom` back to true — so a "Jump to latest" pill left over from
   * a scrolled-up previous conversation doesn't persist after switching.
   * (`forceStickOnNew` governs new messages WITHIN a conversation; this governs
   * switching BETWEEN them.)
   */
  resetKey?: string | number;
  /**
   * Visual edge containing the newest item. Chat timelines use the bottom;
   * newest-first activity feeds use the top while retaining the same
   * no-yank/read-history behaviour.
   */
  latestEdge?: 'top' | 'bottom';
}

/**
 * Auto-scroll a conversation to the bottom while the user is parked there,
 * without yanking them back when they scroll up mid-stream. New message
 * arrivals (count or last id change) force-stick; mid-message token deltas
 * only scroll while `stickToBottom` is still true.
 *
 * Returns the bottom-anchor ref + a `jumpToBottom` action + a `isAtBottom`
 * flag for the optional "Jump to latest" affordance.
 */
export function useStickToBottom<TScroller extends HTMLElement = HTMLElement>({
  scrollerRef: externalScrollerRef,
  tail,
  count,
  threshold = 80,
  awayThreshold = 400,
  forceStickOnNew = true,
  resetKey,
  latestEdge = 'bottom',
}: UseStickToBottomOptions<TScroller>) {
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  // Genuinely away from the tail (past `awayThreshold`) — reading history.
  const [isFarFromBottom, setIsFarFromBottom] = useState(false);
  // A new message landed below while the reader was scrolled up.
  const [hasNewBelow, setHasNewBelow] = useState(false);
  const previousTailRef = useRef({ id: tail?.id, count });
  const resetKeyRef = useRef(resetKey);
  // Our own smooth scroll toward the latest edge is under way. Its in-between positions are not
  // the reader leaving: counting them released the stick mid-glide, so content that grew during it
  // (cards loading above the first messages) was never followed and the room stopped short.
  const glidingRef = useRef(false);
  const glideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // When the reader last touched the scroller (wheel, touch, pointer, key, focus). Only a scroll
  // with that behind it is the reader leaving the latest edge.
  const lastIntentRef = useRef(Number.NEGATIVE_INFINITY);

  const resolveScroller = useCallback((): HTMLElement | null => {
    if (externalScrollerRef?.current) return externalScrollerRef.current;
    let node: HTMLElement | null = bottomAnchorRef.current?.parentElement ?? null;
    while (node) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return node;
      node = node.parentElement;
    }
    return null;
  }, [externalScrollerRef]);

  useEffect(() => {
    const scroller = resolveScroller();
    if (!scroller) return;
    const onScroll = () => {
      const distanceFromLatest =
        latestEdge === 'top'
          ? scroller.scrollTop
          : scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      const atLatest = distanceFromLatest <= threshold;
      if (glidingRef.current) {
        if (!atLatest) return;
        glidingRef.current = false;
      }
      // A scroll nobody asked for — the browser holding its place while cards load above (scroll
      // anchoring), a layout shift — is the layout moving under a parked reader, not the reader
      // leaving. Put them back on the latest edge instead of releasing the stick.
      if (!atLatest && stickyRef.current && performance.now() - lastIntentRef.current > INTENT_WINDOW_MS) {
        scroller.scrollTo({ top: latestEdge === 'top' ? 0 : scroller.scrollHeight, behavior: 'instant' });
        return;
      }
      stickyRef.current = atLatest;
      setIsAtBottom(atLatest);
      setIsFarFromBottom(distanceFromLatest > awayThreshold);
      if (atLatest) setHasNewBelow(false);
    };
    // The reader's hand on the scroller: it ends our glide, and the scrolls that follow are theirs.
    const intent = () => {
      glidingRef.current = false;
      lastIntentRef.current = performance.now();
    };
    const intentEvents = ['wheel', 'touchstart', 'touchmove', 'pointerdown', 'keydown', 'focusin'] as const;
    scroller.addEventListener('scroll', onScroll, { passive: true });
    for (const type of intentEvents) scroller.addEventListener(type, intent, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      for (const type of intentEvents) scroller.removeEventListener(type, intent);
    };
  }, [resolveScroller, threshold, awayThreshold, latestEdge]);

  // 'instant', never 'auto', for a jump: 'auto' means "whatever the element's CSS says", and the
  // app's base layer sets `scroll-behavior: smooth` on every element — so each "jump" animated, and
  // its in-between positions released the stick.
  const scrollToLatest = useCallback(
    (scroller: HTMLElement, behavior: 'instant' | 'smooth') => {
      const distanceFromLatest =
        latestEdge === 'top'
          ? scroller.scrollTop
          : scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      // A glide only when there is somewhere to go, and never for longer than a glide takes — a
      // flag left standing would swallow the reader's own scrolling.
      glidingRef.current = behavior === 'smooth' && distanceFromLatest > threshold;
      if (glideTimerRef.current) clearTimeout(glideTimerRef.current);
      glideTimerRef.current = glidingRef.current
        ? setTimeout(() => {
            glidingRef.current = false;
            glideTimerRef.current = null;
          }, 1500)
        : null;
      scroller.scrollTo({
        top: latestEdge === 'top' ? 0 : scroller.scrollHeight,
        behavior,
      });
    },
    [latestEdge, threshold],
  );

  useEffect(
    () => () => {
      if (glideTimerRef.current) clearTimeout(glideTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    const scroller = resolveScroller();
    if (!scroller) return;
    const previous = previousTailRef.current;
    const isNewMessage = previous.count !== count || previous.id !== tail?.id;
    // The conversation's first page lands where it ends; there is nothing to glide from.
    const firstFill = previous.count === 0 && count > 0;
    previousTailRef.current = { id: tail?.id, count };
    if (isNewMessage && forceStickOnNew) {
      stickyRef.current = true;
      setIsAtBottom(true);
    } else if (!stickyRef.current) {
      // Scrolled-up reader, no force-stick: flag the arrival instead of
      // yanking — the "new messages" affordance is the way back down.
      if (isNewMessage) setHasNewBelow(true);
      return;
    }
    scrollToLatest(scroller, isNewMessage && !firstFill ? 'smooth' : 'instant');
  }, [count, tail?.id, tail?.textLength, resolveScroller, forceStickOnNew, scrollToLatest]);

  // Content can grow without a new message: cards above the timeline that load after it (a
  // Team HQ's intro, glance and rules), a file card or image measuring itself. That moves the
  // latest edge away from a reader who is parked on it, and the room opened on the cards instead
  // of the conversation. Follow the growth while they are parked; a reader who scrolled away
  // stays where they are.
  useEffect(() => {
    const scroller = resolveScroller();
    if (!scroller || typeof ResizeObserver === 'undefined' || typeof MutationObserver === 'undefined') {
      return;
    }
    let frame = 0;
    const follow = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (stickyRef.current) scrollToLatest(scroller, 'instant');
      });
    };
    const resizes = new ResizeObserver(follow);
    const observeChildren = () => {
      for (const child of Array.from(scroller.children)) resizes.observe(child);
    };
    observeChildren();
    const mutations = new MutationObserver(() => {
      observeChildren();
      follow();
    });
    mutations.observe(scroller, { childList: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      resizes.disconnect();
      mutations.disconnect();
    };
  }, [resolveScroller, scrollToLatest]);

  // Conversation switched: re-arm the stick and drop to the newest message,
  // regardless of forceStickOnNew (which only governs new replies within the
  // same conversation). Without this, a "Jump to latest" pill from a
  // scrolled-up channel lingers after switching to another.
  useEffect(() => {
    if (resetKeyRef.current === resetKey) return;
    resetKeyRef.current = resetKey;
    stickyRef.current = true;
    setIsAtBottom(true);
    setIsFarFromBottom(false);
    setHasNewBelow(false);
    const scroller = resolveScroller();
    if (scroller) scrollToLatest(scroller, 'instant');
  }, [resetKey, resolveScroller, scrollToLatest]);

  const jumpToBottom = useCallback(() => {
    const scroller = resolveScroller();
    if (!scroller) return;
    stickyRef.current = true;
    setIsAtBottom(true);
    setIsFarFromBottom(false);
    setHasNewBelow(false);
    scrollToLatest(scroller, 'smooth');
  }, [resolveScroller, scrollToLatest]);

  /** Release the bottom-stick without scrolling — for programmatic
   *  scroll-aways (e.g. scrolling to a highlighted message) that would
   *  otherwise fight token deltas re-bottoming the view. Mirror of
   *  `jumpToBottom`; re-arming is organic via the scroll listener once
   *  the user returns within `threshold` of the bottom. */
  const park = useCallback(() => {
    glidingRef.current = false;
    stickyRef.current = false;
    setIsAtBottom(false);
  }, []);

  return { bottomAnchorRef, isAtBottom, isFarFromBottom, hasNewBelow, jumpToBottom, park };
}
